import React from 'react';

import { gql } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { formatDate, getLocalTimezone, guessIsoTimestamp } from '@modernloop/shared/datetime';
import { Alert, Typography } from '@mui/material';

import {
  ApprovalFeedback_SchedulerFeedbackFragment,
  ApprovalFeedback_TrainedFeedbackFragment,
  ApprovalFeedback_TraineeFeedbackFragment,
  GraduationApproverDecisionType,
  TrainingStatus,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

type Props = {
  role: TrainingStatus;
};

type Fragments = {
  traineeFeedback: ApprovalFeedback_TraineeFeedbackFragment | undefined;
  trainedFeedback: ApprovalFeedback_TrainedFeedbackFragment | undefined;
  schedulerFeedback: ApprovalFeedback_SchedulerFeedbackFragment | undefined;
};

const ApprovalFeedback: FCWithFragments<Fragments, Props> = ({
  traineeFeedback,
  trainedFeedback,
  schedulerFeedback,
  role: status,
}) => {
  const { t } = useTranslation();
  const getStatusFor = () => {
    switch (status) {
      case TrainingStatus.Shadow:
        return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.approvalFeedback.roleReverseShadow');
      case TrainingStatus.ReverseShadow:
        return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.approvalFeedback.roleTrained');
      default:
        return null;
    }
  };

  if (schedulerFeedback?.reviewerId && schedulerFeedback?.decisionType === GraduationApproverDecisionType.MoveForward) {
    return (
      <Alert severity="success" variant="standard">
        <Typography variant="body2" display="inline-flex">
          <Trans
            i18nKey="hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.approvalFeedback.approvedBy"
            values={{
              name: schedulerFeedback.reviewer?.fullName,
              date: formatDate(guessIsoTimestamp(schedulerFeedback.reviewedAt), getLocalTimezone(), 'MMM d, yyyy'),
              role: getStatusFor(),
            }}
          >
            Approved by
            <Typography variant="subtitle2" fontWeight={600}>
              &nbsp;{'{{name}}'}&nbsp;
            </Typography>
            on&nbsp;{'{{date}}'} as {'{{role}}'}
          </Trans>
        </Typography>
      </Alert>
    );
  }

  if (
    traineeFeedback?.reviewerId &&
    trainedFeedback?.reviewerId &&
    traineeFeedback?.decisionType === GraduationApproverDecisionType.MoveForward &&
    trainedFeedback?.decisionType === GraduationApproverDecisionType.MoveForward
  ) {
    return (
      <Alert severity="success" variant="standard">
        <Typography variant="body2" display="inline-flex">
          <Trans
            i18nKey="hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.approvalFeedback.approvedByTwoDates"
            values={{
              name: traineeFeedback.reviewer?.fullName,
              date1: formatDate(guessIsoTimestamp(traineeFeedback.reviewedAt), getLocalTimezone(), 'MMM d, yyyy'),
              date2: formatDate(guessIsoTimestamp(trainedFeedback.reviewedAt), getLocalTimezone(), 'MMM d, yyyy'),
              role: getStatusFor(),
            }}
          >
            Approved by
            <Typography variant="subtitle2" fontWeight={600}>
              &nbsp;{'{{name}}'}&nbsp;
            </Typography>
            on&nbsp;{'{{date1}}'} and&nbsp;{'{{date2}}'} as&nbsp;{'{{role}}'}
          </Trans>
        </Typography>
      </Alert>
    );
  }

  if (traineeFeedback?.decisionType === GraduationApproverDecisionType.MoveForward && !trainedFeedback?.reviewerId) {
    return (
      <Alert severity="success" variant="standard">
        <Typography variant="body2" display="inline-flex">
          <Trans
            i18nKey="hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.approvalFeedback.approvedBy"
            values={{
              name: traineeFeedback.reviewer?.fullName,
              date: formatDate(guessIsoTimestamp(traineeFeedback.reviewedAt), getLocalTimezone(), 'MMM d, yyyy'),
              role: getStatusFor(),
            }}
          >
            Approved by
            <Typography variant="subtitle2" fontWeight={600}>
              &nbsp;{'{{name}}'}&nbsp;
            </Typography>
            on&nbsp;{'{{date}}'} as {'{{role}}'}
          </Trans>
        </Typography>
      </Alert>
    );
  }

  if (trainedFeedback?.decisionType === GraduationApproverDecisionType.MoveForward && !traineeFeedback?.reviewerId) {
    return (
      <Alert severity="success" variant="standard">
        <Typography variant="body2" display="inline-flex">
          <Trans
            i18nKey="hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.approvalFeedback.approvedBy"
            values={{
              name: trainedFeedback.reviewer?.fullName,
              date: formatDate(guessIsoTimestamp(trainedFeedback.reviewedAt), getLocalTimezone(), 'MMM d, yyyy'),
              role: getStatusFor(),
            }}
          >
            Approved by
            <Typography variant="subtitle2" fontWeight={600}>
              &nbsp;{'{{name}}'}&nbsp;
            </Typography>
            on&nbsp;{'{{date}}'} as {'{{role}}'}
          </Trans>
        </Typography>
      </Alert>
    );
  }

  return null;
};

const FeedbackFragment = gql`
  fragment ApprovalFeedback_feedback on ApplicationStageInterviewFeedback {
    reviewerId
    decisionType
    reviewedAt
    reviewer {
      id
      fullName
    }
  }
`;

ApprovalFeedback.fragments = {
  traineeFeedback: gql`
    ${FeedbackFragment}
    fragment ApprovalFeedback_traineeFeedback on ApplicationStageInterviewFeedback {
      ...ApprovalFeedback_feedback
    }
  `,
  trainedFeedback: gql`
    ${FeedbackFragment}
    fragment ApprovalFeedback_trainedFeedback on ApplicationStageInterviewFeedback {
      ...ApprovalFeedback_feedback
    }
  `,
  schedulerFeedback: gql`
    ${FeedbackFragment}
    fragment ApprovalFeedback_schedulerFeedback on ApplicationStageInterviewFeedback {
      ...ApprovalFeedback_feedback
    }
  `,
};

export default ApprovalFeedback;
